import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import { STAGE_BG } from '@/components/researcher/liveDemo/constants'
import type { EmotionStimulus } from '@/services/emotionAnalysisService'
import { youTubeId, youTubeThumb, youTubeThumbMaxRes } from '@/utils/youtube'
import { primeFirstFrame } from '@/utils/primeFirstFrame'
import { stimulusHasVideo } from './stimulusVideo'
import { stageFrameSx } from './stageFrame'
import PrototypeReplay, { type PrototypeReplayData } from './PrototypeReplay'
import YouTubeStage from './YouTubeStage'

/** Fallback for a YouTube stimulus whose player won't load (blocked script,
 *  embedding disabled): the video's thumbnail as a still. Tries the full-res
 *  frame first (not generated for every video) and falls back to hqdefault. */
function YouTubeThumb({ id, title }: { id: string; title: string }) {
  const [src, setSrc] = useState(() => youTubeThumbMaxRes(id))
  return (
    <Box
      component="img"
      src={src}
      alt={title}
      onError={() => setSrc((s) => (s === youTubeThumb(id) ? s : youTubeThumb(id)))}
      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  )
}

/**
 * Visual player for the selected stimulus ("what was shown").
 *
 * Clock ownership: while a real <video> is playing it is the authoritative
 * clock — `onTick` reports its currentTime up to the panel each frame. Chasing
 * an external wall-clock instead (the old design) degenerated into a
 * seek-on-drift loop on network video: the clock ran ahead while the video
 * buffered, every >0.3s drift triggered a seek, each seek stalled the video
 * further, and playback became a one-frame-per-seek slideshow. The panel's
 * wall-clock now only drives stimuli without a playable video (image / text /
 * placeholder — `onVideoFailed` flips a broken source into that mode), and the
 * seek-on-drift sync below only fires on scrubs and replays.
 *
 * `muted` (default `true`) controls whether the <video> stimulus plays with
 * sound; the researcher panel and the respondent results screen both pass
 * `muted={false}` (the panel exposes a mute toggle in its transport bar).
 */
export default function StimulusStage({
  stimulus,
  currentMs,
  playing,
  label,
  muted = true,
  overlay,
  onTick,
  onEnded,
  onVideoFailed,
  onTogglePlay,
  prototypeReplay = null,
  fill = false,
}: {
  stimulus: EmotionStimulus | null
  currentMs: number
  playing: boolean
  label?: string
  muted?: boolean
  /** Rendered on top of the stage (e.g. a face-camera picture-in-picture). */
  overlay?: React.ReactNode
  /** Called each animation frame with the video's time while it plays. */
  onTick?: (ms: number) => void
  /** Called when the video reaches its end. */
  onEnded?: () => void
  /** Called when the video source fails to load (panel falls back to its own clock). */
  onVideoFailed?: () => void
  /** Click-to-play: clicking the stage (or its centre play button) toggles playback. */
  onTogglePlay?: () => void
  /** Figma prototype (Z-98): the screens and one respondent's session to replay. */
  prototypeReplay?: PrototypeReplayData | null
  /** Drop the page height budget: the parent (full-screen view) already sizes
   *  a 16:9 box for the stage, so the frame just fills its width. */
  fill?: boolean
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [failed, setFailed] = useState(false)
  // Latest `playing` for the async first-frame kick (see prime()).
  const playingRef = useRef(playing)
  playingRef.current = playing
  // The first-frame paint runs once per clip; a remounted clip re-arms it.
  const primedRef = useRef(false)

  // A new stimulus gets a fresh chance to load.
  useEffect(() => {
    setFailed(false)
    primedRef.current = false
  }, [stimulus?.id])

  const isVideo = !failed && stimulusHasVideo(stimulus)
  const isImage = !!stimulus && stimulus.type === 'image' && !!stimulus.url
  // YouTube link stimulus: not playable as a <video> (see stimulusHasVideo), so
  // it plays through the IFrame API (YouTubeStage), or as a thumbnail if that fails.
  const ytId = stimulus && stimulus.type === 'url' ? youTubeId(stimulus.url) : null

  // Bare src — no `#t=` media fragment. The first-frame "poster" is painted by
  // seeking to the playhead once the clip loads (see applySeek), not by a fixed
  // fragment: in the merged reel a scrub can land the playhead anywhere inside a
  // clip, and a `#t=0.001` fragment would pin it to the first frame and fight
  // that seek (leaving the wrong frame — or a black one — on screen).
  // The API re-signs stimulus URLs (a fresh S3 `X-Amz-*` querystring) on every
  // poll refresh while analysis is in progress, so `stimulus.url` churns even
  // though the clip is unchanged. Binding that changing string to <video src>
  // makes the browser abort + reload the media on each poll — the visible ~6s
  // blink. Lock the src to the first URL seen for a given clip id; only a real
  // clip change (new id) re-points it, matching the `key={stimulus.id}` remount.
  const lockedSrc = useRef<{ id: EmotionStimulus['id']; url: string } | undefined>(undefined)
  if (isVideo && lockedSrc.current?.id !== stimulus!.id) {
    lockedSrc.current = { id: stimulus!.id, url: stimulus!.url }
  }
  const videoSrc = isVideo ? lockedSrc.current?.url : undefined

  // Reflect play/pause onto the <video>.
  useEffect(() => {
    const v = videoRef.current
    if (!v || !isVideo) return
    if (playing) v.play().catch(() => {})
    else v.pause()
  }, [playing, isVideo, stimulus?.id])

  // Imperatively sync the muted state — React only sets the `muted` attribute
  // on mount in some versions, so dynamic updates need to touch the DOM node.
  useEffect(() => {
    const v = videoRef.current
    if (!v || !isVideo) return
    v.muted = muted
    if (!muted) v.volume = 1
  }, [muted, isVideo, stimulus?.id])

  // While playing, the <video> owns the clock: report its time to the panel.
  // Throttle to ~30 Hz — every rAF tick (60 Hz) pushes a setState that re-renders
  // the whole panel (timeline paths, respondent list), and on heavier machines
  // that main-thread churn starves video compositing into a visible stutter. The
  // video itself still plays at native fps; only the playhead/state updates slow.
  useEffect(() => {
    const v = videoRef.current
    if (!v || !isVideo || !playing || !onTick) return
    let raf = 0
    let lastEmit = 0
    const tick = (now: number) => {
      if (now - lastEmit >= 33) {
        onTick(v.currentTime * 1000)
        lastEmit = now
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [playing, isVideo, onTick, stimulus?.id])

  // The clip-local time we want the <video> parked at, held in a ref so the async
  // media events below (onLoadedMetadata/onLoadedData fire after later renders)
  // always seek to the freshest playhead, never a stale closure value.
  const targetSecRef = useRef(0)
  targetSecRef.current = currentMs / 1000

  // Park the video on the playhead. Floors a hair past 0 so even the first frame
  // decodes and paints — a paused <video> sitting at exactly 0 shows an empty
  // black rectangle until played (this replaces the old `#t=0.001` poster trick).
  // No-op while the video drives the clock (currentMs mirrors it, so drift never
  // crosses the threshold): effectively fires only on scrubs, replays and the
  // clip swaps of the merged reel.
  //
  // `force` (the initial load events) bypasses the drift guard: at the start the
  // playhead sits at 0 and the 0.001 target is sub-threshold, so the seek would
  // never fire — yet that seek is exactly what fetches + decodes the first frame.
  // Without it a network `preload="auto"` clip often loads metadata only, leaving
  // the stage black until the user hits play (which force-loads it). The guard
  // still applies during playback/scrub to avoid a seek-on-drift loop.
  const applySeek = useCallback((force = false) => {
    const v = videoRef.current
    if (!v || !Number.isFinite(v.duration) || v.duration <= 0) return
    const target = Math.min(Math.max(targetSecRef.current, 0.001), v.duration)
    if (force || Math.abs(v.currentTime - target) > 0.25) {
      try {
        v.currentTime = target
      } catch {
        /* not seekable yet — onLoadedMetadata/onLoadedData will retry */
      }
    }
  }, [])

  // Re-seek as the playhead moves (scrub/replay). A freshly-swapped clip has no
  // duration yet, so the seek can't land here — onLoadedMetadata performs it once
  // metadata arrives, which is what un-sticks the black frame after a boundary cross.
  useEffect(() => {
    if (isVideo) applySeek()
  }, [currentMs, isVideo, applySeek])

  // Paint the first frame once the clip loads. A plain force-seek (applySeek)
  // suffices on desktop but does nothing on mobile, where a paused <video> won't
  // decode a frame from a programmatic seek — the stage stays black until the
  // user hits play. primeFirstFrame adds a muted play→pause kick that works
  // there too; restores the caller's mute (this stage runs unmuted on the
  // respondent results screen). Skipped while already playing — the play/pause
  // effect paints it then.
  const prime = useCallback(() => {
    const v = videoRef.current
    if (!v || primedRef.current) return
    primedRef.current = true
    if (playingRef.current) {
      applySeek(true)
      return
    }
    void primeFirstFrame(v, targetSecRef.current, () => !playingRef.current)
  }, [applySeek])

  return (
    <Box
      sx={{
        position: 'relative',
        // The shared player frame: 16:9, but never taller than the height
        // budget — on a short or very wide window the stage narrows and centres
        // instead of growing past the fold (see `stageFrame`).
        ...stageFrameSx,
        ...(fill && { maxWidth: 'none' }),
        bgcolor: STAGE_BG,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      {isVideo ? (
        <Box
          // Key by clip so each segment of the merged reel mounts a CLEAN <video>
          // rather than mutating src on a reused element. Re-pointing src mid-load
          // / mid-seek on a network clip (exactly what scrubbing across a boundary
          // does) strands the element in a stuck, black "seeking" state; a fresh
          // element loads cleanly every time.
          key={stimulus!.id}
          component="video"
          ref={videoRef}
          src={videoSrc}
          muted={muted}
          playsInline
          preload="auto"
          onLoadedMetadata={() => prime()}
          onLoadedData={() => prime()}
          onEnded={onEnded}
          onError={() => {
            // A rapid clip swap (scrubbing across a merged-reel boundary) aborts the
            // in-flight load — transient, not a broken source — so it must NOT trip
            // the permanent video-failed fallback, which would be hard to recover from.
            if (videoRef.current?.error?.code === MediaError.MEDIA_ERR_ABORTED) return
            setFailed(true)
            onVideoFailed?.()
          }}
          sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : stimulus && stimulus.type === 'figma' && prototypeReplay ? (
        <PrototypeReplay replay={prototypeReplay} currentMs={currentMs} />
      ) : isImage ? (
        <Box component="img" src={stimulus!.url} alt={stimulus!.title} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : ytId && !failed ? (
        <YouTubeStage
          key={ytId}
          videoId={ytId}
          currentMs={currentMs}
          playing={playing}
          muted={muted}
          onTick={onTick}
          onEnded={onEnded}
          onFailed={() => {
            setFailed(true)
            onVideoFailed?.()
          }}
        />
      ) : ytId ? (
        <YouTubeThumb key={ytId} id={ytId} title={stimulus!.title} />
      ) : stimulus && stimulus.type === 'text' ? (
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: 22, md: 34 }, textAlign: 'center', px: 4 }}>
          {stimulus.payload}
        </Typography>
      ) : (
        <Box sx={{ textAlign: 'center', color: 'rgba(255,255,255,.6)', px: 3 }}>
          <MovieOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />
          <Typography sx={{ color: 'rgba(255,255,255,.85)', fontWeight: 700, fontSize: 15 }}>
            {stimulus?.title || label || ''}
          </Typography>
          <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>{label}</Typography>
        </Box>
      )}
      {overlay}
      {/* Click-to-play affordance: a centred play badge over the still frame so
          it is obvious the stage can be played. Hidden once playback starts. */}
      {onTogglePlay && !playing && (
        <Box
          onClick={onTogglePlay}
          role="button"
          aria-label="Odtwórz"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 5,
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            bgcolor: 'rgba(0,0,0,.18)',
            transition: 'background-color .15s ease',
            '&:hover': { bgcolor: 'rgba(0,0,0,.28)' },
          }}
        >
          <Box
            sx={{
              width: { xs: 64, md: 76 },
              height: { xs: 64, md: 76 },
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255,255,255,.92)',
              boxShadow: '0 6px 22px rgba(0,0,0,.35)',
            }}
          >
            <PlayArrowRoundedIcon sx={{ fontSize: { xs: 40, md: 48 }, color: '#16172e', ml: 0.4 }} />
          </Box>
        </Box>
      )}
    </Box>
  )
}
